import { exportTableToPdf } from '../exportTableToPdf';
import { getIrlPdfCellStyle, IRL_BADGE_COLUMNS } from '../pdfCellStyles';
import {
    filterRowsByIds,
    formatPdfDate,
    formatPlainText,
    getVisibleColumnsFromState,
    stripHtmlToPlainText,
} from '../pdfTextUtils';

const IRL_COLUMN_ORDER = [
    'number',
    'status',
    'workstream',
    'tag',
    'request',
    'priority',
    'requestedOn',
    'addedBy',
    'response',
    'respondedOn',
    'respondent',
    'dataroom',
];

const COLUMN_WEIGHTS = {
    number: 1.2,
    status: 1,
    workstream: 1.3,
    tag: 1,
    request: 3,
    priority: 0.9,
    requestedOn: 1.1,
    addedBy: 1.2,
    response: 3,
    respondedOn: 1.1,
    respondent: 1.2,
    dataroom: 0.8,
};

const flattenIrlRows = (rows, depth = 0) => {
    const result = [];
    (rows || []).forEach(row => {
        result.push({ ...row, _depth: depth });
        if (row.childrens?.length) {
            result.push(...flattenIrlRows(row.childrens, depth + 1));
        }
    });
    return result;
};

const isResponded = row => Boolean(row?.irresponse);

export const exportIrlToPdf = async ({
    rows,
    columnState,
    workstreams = [],
    projectUsersAll = [],
    projectName,
    selectedRowIds = null,
    filename = 'irl.pdf',
}) => {
    const visibleColumns = getVisibleColumnsFromState(columnState).sort(
        (a, b) => IRL_COLUMN_ORDER.indexOf(a.key) - IRL_COLUMN_ORDER.indexOf(b.key),
    );

    const flatRows = flattenIrlRows(rows);
    const filteredRows = filterRowsByIds(flatRows, selectedRowIds);

    const getWorkstreamName = id => formatPlainText(workstreams.find(ws => ws.id === id)?.name || '');
    const getUserName = userId =>
        formatPlainText(projectUsersAll.find(u => u?.value?.user_id === userId)?.value?.full_name || '');

    const getCellValue = (row, columnKey) => {
        const indent = row._depth > 0 ? `${'  '.repeat(row._depth)}- ` : '';
        switch (columnKey) {
            case 'number':
                return `${indent}${formatPlainText(row.number || row.id || '')}`;
            case 'status':
                return isResponded(row) ? 'Responded' : 'Pending';
            case 'workstream':
                return getWorkstreamName(row.workstream);
            case 'tag':
                return formatPlainText(row.tag || '');
            case 'request':
                return stripHtmlToPlainText(row.request || '');
            case 'priority':
                return formatPlainText(row.priorities || '');
            case 'requestedOn':
                return formatPdfDate(row.created);
            case 'addedBy':
                return getUserName(row.created_by);
            case 'response':
                return stripHtmlToPlainText(row?.irresponse?.answer || '');
            case 'respondedOn':
                return formatPdfDate(row?.irresponse?.created);
            case 'respondent':
                return getUserName(row?.irresponse?.created_by);
            case 'dataroom':
                return row?.referenced_file ? 'Yes' : '';
            default:
                return '';
        }
    };

    await exportTableToPdf({
        title: 'Information Requests',
        projectName,
        visibleColumns,
        rows: filteredRows,
        getCellValue,
        getCellStyle: (row, columnKey) => getIrlPdfCellStyle(columnKey, row),
        columnWeights: COLUMN_WEIGHTS,
        badgeColumns: IRL_BADGE_COLUMNS,
        filename,
    });
};
